import React, { useEffect, useState } from "react";

const API_BASE = "https://api.hfadigitalcreations.com";

const ProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [loadingProjectId, setLoadingProjectId] = useState(null);

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/project/all`);
      const data = await res.json();
      if (data.status) {
        setProjects(data.data);
        setFilteredProjects(data.data);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  // Search filter
  useEffect(() => {
    if (!search.trim()) {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(
        projects.filter((proj) =>
          proj.projecttitle.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, projects]);

  // Quick View
  const fetchSingleProject = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/project/${id}`);
      const data = await res.json();
      if (data.status) {
        setSelectedProject(data.data);
        setIsModalOpen(true);
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error("Error fetching project details:", err);
    }
  };

  // Approve
  const approveProject = async (project) => {
    setLoadingProjectId(project.id);
    try {
      const res = await fetch(`${API_BASE}/project/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: project.id, user_id: project.user_id }),
      });
      const data = await res.json();
      if (data.status) {
        fetchProjects();
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingProjectId(null);    
    }
  };

  // Decline (create API same as approve with is_approved = 2)
  const declineProject = async (project) => {
    setLoadingProjectId(project.id);
    try {
      const res = await fetch(`${API_BASE}/project/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: project.id, user_id: project.user_id }),
      });
      const data = await res.json();
      if (data.status) {
        fetchProjects();
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingProjectId(null);
    }
  };

  // Export CSV
  const exportCSV = () => {
    if (!projects.length) {
      alert("No data to export");
      return;
    }

    const headers = [
      "ID",
      "Project Title",
      "Budget",
      "Timeline",
      "Approval Status",
    ];

    const rows = projects.map((proj) => [
      proj.id,
      proj.projecttitle,
      proj.projectbudget,
      `${proj.projecttimelinefrom} to ${proj.projecttimelineto}`,
      proj.is_approved === "1"
        ? "Approved"
        : proj.is_approved === "2"
        ? "Rejected"
        : "Pending",
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) =>
          row
            .map((item) =>
              typeof item === "string" && item.includes(",")
                ? `"${item}"`
                : item
            )
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "projects_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Project Requests</h2>
        <div className="flex gap-2">
          <button className="border px-4 py-2 rounded hover:bg-gray-100">
            Filter
          </button>
          <button className="border px-4 py-2 rounded hover:bg-gray-100">
            Date Range
          </button>
          <button
            onClick={exportCSV}
            className="border px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search projects..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="border rounded w-full p-2 mb-4"
      />

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map((proj) => (
          <div
            key={proj.id}
            className="border rounded p-4 bg-white shadow-sm flex flex-col justify-between"
          >
            <div>
              <h3 className="font-semibold text-lg">{proj.projecttitle}</h3>
              <p className="text-gray-500 text-sm">Budget: {proj.projectbudget}</p>
              <span
                className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                  proj.is_approved === "1"
                    ? "bg-green-100 text-green-700"
                    : proj.is_approved === "2"
                    ? "bg-red-100 text-red-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {proj.is_approved === "1"
                  ? "Approved"
                  : proj.is_approved === "2"
                  ? "Rejected"
                  : "Pending"}
              </span>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => fetchSingleProject(proj.id)}
                className="text-blue-600 hover:underline"
              >
                Quick View
              </button>
              <div className="flex gap-2">
                {proj.is_approved !== "1" && (
                  <button
                    onClick={() => approveProject(proj)}
                    disabled={loadingProjectId === proj.id}
                    className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 disabled:opacity-50"
                  >
                    Approve
                  </button>
                )}
                {proj.is_approved !== "2" && (
                  <button
                    onClick={() => declineProject(proj)}
                    disabled={loadingProjectId === proj.id}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 disabled:opacity-50"
                  >
                    Decline
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded p-6 w-full max-w-md shadow-lg relative">
            <h3 className="text-lg font-semibold mb-2">
              {selectedProject.projecttitle}
            </h3>
            <p className="text-sm mb-2">
              <strong>Budget:</strong> {selectedProject.projectbudget}
            </p>
            <p className="text-sm mb-2">
              <strong>Description:</strong> {selectedProject.projectdescription}
            </p>
            <p className="text-sm mb-2">
              <strong>Timeline:</strong>{" "}
              {selectedProject.projecttimelinefrom} to{" "}
              {selectedProject.projecttimelineto}
            </p>

            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectsTab;
